import type { Melody, MelodyEntityNote, Scale } from '@tunasong/schemas'
import { midiToNoteLabel } from '../analysis/pitch-training.js'
import { displayAccidentals } from '../chord/concert-pitch.js'
import { barBeats, GRID_TOLERANCE } from './grid.js'
import { spellMelodyNote, type MelodyNoteRole } from './note-roles.js'
import { roleDescription } from './why-line.js'

/**
 * The spoken name of a note on the roll - "E4, beat 2 and a half, 12 ms late" (§4.1).
 *
 * A screen-reader user needs the concept, not the pixels: which pitch, where in the bar, and how
 * far off the grid it sits. The offset is the whole reason this entity exists, so it is spoken
 * whenever it rounds to a millisecond or more.
 *
 * Bar 1 is omitted - a short melody that never leaves its first bar should not say "bar 1" on
 * every note.
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §4.1
 */

/** Sub-beat positions the four grids can produce, as the words a musician uses. */
const SUB_BEAT_WORDS: { fraction: number; words: string }[] = [
  { fraction: 1 / 6, words: ' and a sixth' },
  { fraction: 1 / 4, words: ' and a quarter' },
  { fraction: 1 / 3, words: ' and a third' },
  { fraction: 1 / 2, words: ' and a half' },
  { fraction: 2 / 3, words: ' and two thirds' },
  { fraction: 3 / 4, words: ' and three quarters' },
  { fraction: 5 / 6, words: ' and five sixths' },
]

const subBeatWords = (fraction: number): string =>
  SUB_BEAT_WORDS.find(entry => Math.abs(entry.fraction - fraction) <= GRID_TOLERANCE)?.words ?? ''

/**
 * The note's name as its role spells it ("B♭4"), so the name and the why line agree; without a role, as
 * the same speller spells it. The octave follows the letter: C♭5 sounds as B4, and B♯3 as C4.
 */
const spelledLabel = (midi: number, name: string | undefined) => {
  if (!name) {
    return midiToNoteLabel(midi)
  }
  const pitchClass = ((midi % 12) + 12) % 12
  const letter = name[0]
  const octaveShift = letter === 'C' && pitchClass === 11 ? 1 : letter === 'B' && pitchClass === 0 ? -1 : 0
  return `${displayAccidentals(name)}${Math.floor(midi / 12) - 1 + octaveShift}`
}

export interface DescribeMelodyNoteOptions {
  /**
   * The note's roles (melody toolkit M3; spec §10), one per chord it is judged against: "C5, bar 2 beat 1,
   * over G: the 4th, wants to fall to B."
   */
  roles?: MelodyNoteRole[]
  key?: Scale
}

export const describeMelodyNote = (
  note: MelodyEntityNote,
  melody: Melody,
  options: DescribeMelodyNoteOptions = {}
): string => {
  const perBar = barBeats(melody.tempo.meter)
  const bar = Math.floor(note.onset / perBar) + 1
  const withinBar = note.onset - (bar - 1) * perBar
  const beat = Math.floor(withinBar) + 1
  const position = `${bar > 1 ? `bar ${bar} ` : ''}beat ${beat}${subBeatWords(withinBar - Math.floor(withinBar))}`

  const roundedOffset = Math.round(Math.abs(note.offsetMs))
  const offset = roundedOffset === 0 ? '' : `, ${roundedOffset} ms ${note.offsetMs > 0 ? 'late' : 'early'}`

  const { roles = [], key } = options
  /** One speller for every name: a role's, or with no role the same rules (Bowie, 2026-10-23) */
  const index = melody.notes.indexOf(note)
  const name = roles[0]?.name ?? (index >= 0 ? spellMelodyNote(melody, index, key ?? melody.key) : undefined)
  const base = `${spelledLabel(note.midi, name)}, ${position}${offset}`
  return roles.length === 0 ? base : `${base}, ${roles.map(role => roleDescription(role, key)).join('; ')}.`
}
